/**
 * 導航權限映射
 * 
 * 定義 Sidebar 選單項目與權限模組的對應關係
 * 
 * @module NavigationPermissions
 */

import type { AdminRole } from './roles';
import type { Module } from './permissions';
import { hasPermission } from './permissions';

/**
 * 導航項目配置
 */
export interface NavItem {
  path: string;
  icon: string;
  label: string;
  module: Module;
  children?: NavItem[];
}

/**
 * 導航分組配置
 */
export interface NavGroup {
  id: string;
  label: string;
  items: NavItem[];
}

/**
 * 完整導航配置
 * label 使用 i18n key，格式為 adminNav.xxx
 */
export const navigationConfig: NavGroup[] = [
  {
    id: 'frontendVisual',
    label: 'adminNavGroups.frontendVisual',
    items: [
      {
        path: '/admin/cms',
        icon: 'ri-pages-line',
        label: 'adminNav.cms',
        module: 'cms',
      },
      {
        path: '/admin/frontend-images',
        icon: 'ri-image-line',
        label: 'adminNav.frontendImages',
        module: 'frontend_images',
      },
      {
        path: '/admin/service-cases',
        icon: 'ri-video-line',
        label: 'adminNav.serviceCases',
        module: 'service_cases',
      },
      {
        path: '/admin/milestones',
        icon: 'ri-timeline-view',
        label: 'adminNav.milestones',
        module: 'milestones',
      },
    ],
  },
  {
    id: 'dailyOps',
    label: 'adminNavGroups.dailyOps',
    items: [
      {
        path: '/admin/dashboard',
        icon: 'ri-dashboard-line',
        label: 'adminNav.dashboard',
        module: 'dashboard',
      },
      {
        path: '/admin/orders',
        icon: 'ri-file-list-3-line',
        label: 'adminNav.orders',
        module: 'orders',
      },
      {
        path: '/admin/locations',
        icon: 'ri-map-pin-line',
        label: 'adminNav.locations',
        module: 'locations',
      },
      {
        path: '/admin/vehicles',
        icon: 'ri-car-line',
        label: 'adminNav.vehicles',
        module: 'vehicles',
      },
      {
        path: '/admin/attendance',
        icon: 'ri-user-follow-line',
        label: 'adminNav.attendance',
        module: 'attendance',
      },
      {
        path: '/admin/schedules',
        icon: 'ri-calendar-schedule-line',
        label: 'adminNav.schedules',
        module: 'schedules',
      },
    ],
  },
  {
    id: 'muviSpace',
    label: 'adminNav.muviSpace',
    items: [
      {
        path: '/admin/muvi-space/bookings',
        icon: 'ri-building-4-line',
        label: 'adminNav.muviSpace',
        module: 'muvi_bookings',
        children: [
          {
            path: '/admin/muvi-space/bookings',
            icon: '',
            label: 'adminNav.muviBookings',
            module: 'muvi_bookings',
          },
          {
            path: '/admin/muvi-space/content',
            icon: '',
            label: 'adminNav.muviContent',
            module: 'muvi_content',
          },
          {
            path: '/admin/muvi-space/settings',
            icon: '',
            label: 'adminNav.muviSettings',
            module: 'muvi_settings',
          },
        ],
      },
    ],
  },
  {
    id: 'productManagement',
    label: 'adminNavGroups.productManagement',
    items: [
      {
        path: '/admin/equipment',
        icon: 'ri-camera-line',
        label: 'adminNav.equipment',
        module: 'equipment',
        children: [
          {
            path: '/admin/equipment',
            icon: '',
            label: 'adminNav.equipmentList',
            module: 'equipment',
          },
          {
            path: '/admin/equipment/items',
            icon: '',
            label: 'adminNav.equipmentItems',
            module: 'equipment_items',
          },
          {
            path: '/admin/categories',
            icon: '',
            label: 'adminNav.categories',
            module: 'categories',
          },
          {
            path: '/admin/brands',
            icon: '',
            label: 'adminNav.brands',
            module: 'brands',
          },
          {
            path: '/admin/accessories',
            icon: '',
            label: 'adminNav.accessories',
            module: 'accessories',
          },
        ],
      },
    ],
  },
  {
    id: 'customerManagement',
    label: 'adminNavGroups.customerManagement',
    items: [
      {
        path: '/admin/customers',
        icon: 'ri-user-line',
        label: 'adminNav.customers',
        module: 'customers',
      },
      {
        path: '/admin/forms',
        icon: 'ri-file-list-line',
        label: 'adminNav.forms',
        module: 'forms',
      },
    ],
  },
  {
    id: 'legalContracts',
    label: 'adminNavGroups.legalContracts',
    items: [
      {
        path: '/admin/contracts',
        icon: 'ri-file-text-line',
        label: 'adminNav.contracts',
        module: 'contracts',
      },
      {
        path: '/admin/legal-settings',
        icon: 'ri-building-2-line',
        label: 'adminNav.legalSettings',
        module: 'legal_settings',
      },
    ],
  },
  {
    id: 'accountingReports',
    label: 'adminNavGroups.accountingReports',
    items: [
      {
        path: '/admin/analytics',
        icon: 'ri-bar-chart-box-line',
        label: 'adminNav.analytics',
        module: 'analytics',
      },
      {
        path: '/admin/urent',
        icon: 'ri-coin-line',
        label: 'adminNav.urent',
        module: 'urent',
        children: [
          {
            path: '/admin/urent',
            icon: '',
            label: 'adminNav.urentApplications',
            module: 'urent',
          },
          {
            path: '/admin/urent/ledger',
            icon: '',
            label: 'adminNav.urentLedger',
            module: 'urent_ledger',
          },
          {
            path: '/admin/urent-contracts',
            icon: '',
            label: 'adminNav.urentContracts',
            module: 'urent_contracts',
          },
        ],
      },
      {
        path: '/admin/prepaid',
        icon: 'ri-wallet-3-line',
        label: 'adminNav.prepaid',
        module: 'prepaid',
        children: [
          {
            path: '/admin/prepaid',
            icon: '',
            label: 'adminNav.prepaidApplications',
            module: 'prepaid',
          },
          {
            path: '/admin/prepaid/customers',
            icon: '',
            label: 'adminNav.prepaidCustomers',
            module: 'prepaid_customers',
          },
          {
            path: '/admin/prepaid/ledger',
            icon: '',
            label: 'adminNav.prepaidLedger',
            module: 'prepaid_ledger',
          },
          {
            path: '/admin/prepaid/plans',
            icon: '',
            label: 'adminNav.prepaidPlans',
            module: 'prepaid_plans',
          },
        ],
      },
    ],
  },
  {
    id: 'systemManagement',
    label: 'adminNavGroups.systemManagement',
    items: [
      {
        path: '/admin/admins',
        icon: 'ri-admin-line',
        label: 'adminNav.admins',
        module: 'admins',
      },
      {
        path: '/admin/system-governance',
        icon: 'ri-shield-star-line',
        label: 'adminNav.systemGovernance',
        module: 'system_governance',
      },
      {
        path: '/admin/ai-agent',
        icon: 'ri-robot-2-line',
        label: 'adminNav.aiAgent',
        module: 'ai_agent',
      },
      {
        path: '/admin/settings',
        icon: 'ri-settings-3-line',
        label: 'adminNav.settings',
        module: 'settings',
        children: [
          {
            path: '/admin/settings/google-calendar',
            icon: '',
            label: 'adminNav.googleCalendar',
            module: 'google_calendar',
          },
          {
            path: '/admin/settings/line-notify',
            icon: '',
            label: 'adminNav.lineNotify',
            module: 'line_notify',
          },
          {
            path: '/admin/settings/api',
            icon: '',
            label: 'adminNav.apiSettings',
            module: 'api_settings',
          },
          {
            path: '/admin/settings/storefront-sort',
            icon: '',
            label: 'adminNav.storefrontSort',
            module: 'storefront_sort',
          },
        ],
      },
    ],
  },
];

function getVisibleNavItem(role: AdminRole, item: NavItem): NavItem | null {
  if (!hasPermission(role, item.module, 'view_menu')) {
    return null;
  }

  if (item.children) {
    return {
      ...item,
      children: item.children
        .map((child) => getVisibleNavItem(role, child))
        .filter((child): child is NavItem => child !== null),
    };
  }

  return item;
}

/**
 * 根據角色過濾導航項目
 * 
 * @param role - 角色
 * @returns 該角色可見的導航分組；沒有可見 child 的 group 不回傳
 */
export function getNavigationForRole(role: AdminRole): NavGroup[] {
  return navigationConfig
    .map((group) => ({
      ...group,
      items: group.items
        .map((item) => getVisibleNavItem(role, item))
        .filter((item): item is NavItem => item !== null),
    }))
    .filter((group) => group.items.length > 0);
}

/**
 * 檢查角色是否可以看到特定導航項目
 * 
 * @param role - 角色
 * @param path - 路徑
 * @returns true 如果可見
 */
export function canViewNavigation(role: AdminRole, path: string): boolean {
  const findItem = (items: NavItem[]): NavItem | undefined => {
    for (const item of items) {
      if (item.path === path) return item;
      if (item.children) {
        const found = findItem(item.children);
        if (found) return found;
      }
    }
    return undefined;
  };

  const item = navigationConfig
    .map((group) => findItem(group.items))
    .find((found): found is NavItem => found !== undefined);
  if (!item) return false;

  return hasPermission(role, item.module, 'view_menu');
}
